export interface PrimeNgPageEventLike {
  first?: number; // index of first record in current page (0-based)
  rows?: number;  // rows per page
  page?: number;  // current page index (0-based)
}

export interface BackendPaginationParams {
  page: number; // 1-based
  limit: number; // per-page
  first?: number;
  rows?: number;
}

export interface IPaginationMeta {
  page: number,
  limit: number,
  total: number,
  totalPages: number,
  hasNextPage: boolean,
  hasPreviousPage: boolean
}

export function normalizePrimeNgPageEvent(event: PrimeNgPageEventLike): BackendPaginationParams {
  const first = event?.first ?? 0;
  const rows = event?.rows ?? 10;
  const zeroBasedPage = typeof event?.page === 'number' ? event.page : Math.floor(first / rows);
  const page = zeroBasedPage + 1; // convert to 1-based for backend
  return { page, limit: rows, first, rows };
}

export function computeFirstFromPage(page: number, limit: number): number {
  const safePage = Math.max(1, Number(page) || 1);
  const safeLimit = Math.max(1, Number(limit) || 10);
  return (safePage - 1) * safeLimit;
}

export function extractTotalRecords(response: any): number {
  if (!response) return 0;
  if (typeof response?.data?.total === 'number') return response.data.total;
  if (typeof response?.total === 'number') return response.total;
  if (Array.isArray(response?.data)) return response.data.length;
  if (Array.isArray(response)) return response.length;
  return 0;
}

export function extractPaginationMeta(response: any): any {
  // Extract pagination metadata from backend response
  if (!response) return null;

  // Check if pagination meta is in response.data or response directly
  const meta = response?.data?.pagination || response?.pagination || response;

  if (meta && typeof meta === 'object' &&
    ('page' in meta || 'limit' in meta || 'total' in meta)) {
    return meta;
  }

  return null;
}

export function createPaginationState(meta: any, defaultRows: number = 10): { first: number, rows: number } {
  if (!meta) return { first: 0, rows: defaultRows };

  const page = parseInt(meta.page) || 1;
  const limit = parseInt(meta.limit) || defaultRows;

  return {
    first: (page - 1) * limit,
    rows: limit
  };
}

export function handlePageSizeChange(newPageSize: number, currentFirst: number, currentRows: number): { first: number, page: number } {
  // When page size changes, we typically want to stay as close as possible to the current position
  // Calculate which record we're currently viewing
  const currentRecord = currentFirst + 1;

  // Calculate what page that record would be on with the new page size
  const newPage = Math.ceil(currentRecord / newPageSize);

  // Calculate the new first index
  const newFirst = (newPage - 1) * newPageSize;

  return {
    first: newFirst,
    page: newPage - 1 // 0-based for PrimeNG
  };
}


